/**
 * Where you left off in each stage — and whether the app is allowed to remember.
 *
 * The behaviour: click Deliver, and instead of the sites list you land back in the site
 * design you were editing before you wandered off to Research. Four nav entries work this
 * way (Subjects, Brainstorms, News Beats, Pages, Sites & deploys); each keeps ONE url.
 *
 * IT IS OFF BY DEFAULT, and that is the point of this module existing at all. Restoring a
 * drill-down is great when the nav click meant "back to what I was doing" and wrong when
 * it meant "show me the list" — and which one you meant is not knowable from the click.
 * Landing somewhere you did not ask for is the more confusing failure of the two, so the
 * app does the literal thing until you say otherwise.
 *
 * OFF MEANS NOT RECORDED, not merely not restored. A preference that silently kept
 * writing down every screen you visited, waiting to be switched on, is not what "off"
 * looks like to the person reading the checkbox.
 *
 * Pure and dependency-free so it can be unit-tested: CRA's jest cannot resolve
 * react-router-dom v7, so anything imported by a component that imports the router is
 * untestable in isolation.
 */

/** One key per sticky nav entry, holding the last url visited inside it. */
const PLACE_PREFIX = 'jcseo_nav_state_';

/** The opt-in. Anything other than 'yes' — including absent — is off. */
export const RESUME_KEY = 'jcseo_resume_where_you_left_off';

// localStorage throws rather than returning null in some private modes, and a nav that
// cannot render because it could not read a preference is a bad trade for a convenience.
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* nothing to do — the preference just does not persist */
  }
}

/** Storage key for one entry's remembered place. Exported for tests and for nothing else. */
export function placeKey(basePath: string): string {
  return PLACE_PREFIX + basePath;
}

export function resumeEnabled(): boolean {
  return read(RESUME_KEY) === 'yes';
}

/**
 * Turn the memory on or off.
 *
 * Switching OFF forgets everything already saved. Keeping it would mean that switching
 * back on weeks later teleports you into whatever you happened to be looking at the day
 * you turned it off, which reads as a bug rather than as a preference being honoured.
 */
export function setResumeEnabled(on: boolean): void {
  write(RESUME_KEY, on ? 'yes' : null);
  if (!on) forgetAllPlaces();
}

/** Save the place, if the user has asked for places to be saved. */
export function rememberPlace(basePath: string, url: string): void {
  if (!resumeEnabled()) return;
  write(placeKey(basePath), url);
}

/** The saved place, or null — including whenever the preference is off. */
export function recallPlace(basePath: string): string | null {
  if (!resumeEnabled()) return null;
  return read(placeKey(basePath));
}

/** Drop every remembered place. Scans rather than taking a list, so it cannot fall out of
 *  step with the nav's own idea of which entries are sticky. */
export function forgetAllPlaces(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(PLACE_PREFIX)) doomed.push(key);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* see read() */
  }
}
